import {
  TriplePlayHowItWorks,
  TriplePlayProductSupplier,
} from '@kizi/comparator-core';
import { Div, P } from '@kizi/core-components';
import {
  IconV2,
  TimelineItemProps,
  withTooltip,
} from '@kizi/shared-components';
import React from 'react';
import { HOW_IT_WORKS } from '../../../../../funnels/home';
import { AfterTheSwitch } from '../../../../AfterTheSwitch';
import {
  ContractSwitchFaq,
  ContractSwitchFaqItem,
} from '../../../../Product/ExpandedPart/ContractSwitchFaq';

const TooltipIcon = withTooltip(IconV2);

interface SwitchTabProps {
  supplier: TriplePlayProductSupplier;
  title?: string;
  subtitle?: string;
  type?: string;
}

const QuestionNames = {
  'Task-division-q1': 'Overstapservice',
  'Task-division-q2': 'Al internet op je huidige adres?',
  'Task-division-q3': 'Zelf installeren of een monteur?',
};

export const filterTaskDivisions = (
  taskDivisions: TriplePlayProductSupplier['taskDivisions'],
): ContractSwitchFaqItem[] => {
  if (!taskDivisions) {
    return [];
  }

  return taskDivisions
    .map(({ name, value }) => ({
      label: QuestionNames[name] || '',
      content: value || '',
    }))
    .filter(({ label, content }) => label && content);
};

export const TaskDivisions: React.FC<SwitchTabProps> = ({ supplier }) => {
  const { taskDivisions, name } = supplier;

  if (!taskDivisions) {
    return null;
  }

  const accordionItems: ContractSwitchFaqItem[] =
    filterTaskDivisions(taskDivisions);

  return (
    <Div className="tab-section">
      <P mb={3} variant="bodyImportant">
        Overstappen naar {name}
      </P>
      <P variant="footnoteRegular" mb={6}>
        Heb je een abonnement gevonden dat bij jou past? Dan sturen wij je
        aanvraag door naar de provider. Je ontvangt vervolgens binnen een paar
        dagen je contractbevestiging. Vanaf dat moment heb je 14 dagen
        wettelijke bedenktijd waarin je kosteloos je aanvraag mag annuleren.
      </P>
      <ContractSwitchFaq items={accordionItems} />
    </Div>
  );
};

const Titles = {
  [HOW_IT_WORKS.AFFILIATE]: 'Aanvraag op de website van verzekeraar',
  [HOW_IT_WORKS.CONFIRMATION_CB]: 'Bevestiging Consumentenbond',
  [HOW_IT_WORKS.CANCEL_OLD]: 'Opzeggen oude verzekering',
  [HOW_IT_WORKS.SERVICE]: 'Overstapservice',
  [HOW_IT_WORKS.DELIVERY]: 'Afspraak levering & installatie',
};

const OsTriplePlayTitles = {
  [HOW_IT_WORKS.AFFILIATE]: 'Aanvraag op de website van verzekeraar',
  [HOW_IT_WORKS.CONFIRMATION_CB]: 'Kies je provider',
  [HOW_IT_WORKS.CANCEL_OLD]: 'Opzeggen oude verzekering',
  [HOW_IT_WORKS.SERVICE]: 'Opzeggen huidige pakket',
  [HOW_IT_WORKS.DELIVERY]: 'Klaar',
};

function getStep(
  step: TriplePlayHowItWorks,
  name?: string,
  type?: string,
): TimelineItemProps {
  if (step.typeName === HOW_IT_WORKS.CONFIRMATION_COMPANY) {
    return {
      title: `Bevestiging ${name}`,
      body: step.value,
    };
  }

  return {
    title:
      type === 'os-triple-play'
        ? OsTriplePlayTitles[step.typeName]
        : Titles[step.typeName],
    body: step.value,
  };
}

export const HowItWorks: React.FC<SwitchTabProps> = ({
  supplier,
  title = 'Zo werkt je aanvraag',
  subtitle = 'Beste deal gevonden? Hieronder lees je wat je kunt verwachten na het aanvragen van je abonnement.',
  type,
}) => {
  const { howItWorks, name } = supplier;

  if (!howItWorks?.length) {
    return null;
  }

  const steps = howItWorks.map((step) => getStep(step, name, type));

  return <AfterTheSwitch title={title} subtitle={subtitle} steps={steps} />;
};

export const SwitchTab3P: React.FC<{
  supplier: TriplePlayProductSupplier | undefined;
}> = ({ supplier }) => {
  if (!supplier) {
    return null;
  }

  return (
    <Div display={[null, null, 'flex']} variant="productTabSwitch">
      <Div className="tab-section">
        <TaskDivisions supplier={supplier} />
      </Div>
      <HowItWorks supplier={supplier} />
    </Div>
  );
};

export const SwitchTabOS3P = ({ supplier, averageDeliveryTimeInDays }) => {
  if (!supplier) {
    return null;
  }

  return (
    <Div display={[null, null, 'flex']} variant="productTabSwitch">
      <Div className="tab-section" mb={6}>
        <Div className="tab-section">
          <P variant="bodyImportant" fontSize="20px!important" lineHeight={1}>
            {'Zo gaat je aanvraag'}
          </P>
          <P mb={3} variant="footnoteRegular" lineHeight="24px">
            {
              'Beste deal gevonden? Hieronder lees je wat je kunt verwachten na het aanvragen van je contract.'
            }
          </P>
          {averageDeliveryTimeInDays && (
            <Div
              py={2}
              display="flex"
              justifyContent="space-between"
              borderBottom="1px solid #F0F0EF"
            >
              <P variant="bodyImportant">{'Levertijd'}</P>
              <Div display="flex" gridGap={2}>
                <P variant="bodyRegular">{`${averageDeliveryTimeInDays} dagen`}</P>
                <TooltipIcon
                  name="info"
                  size={22}
                  css={{ fill: 'second.0' }}
                  _tooltip={
                    'De gemiddelde levertijd vanaf de aanvraag tot de levering.'
                  }
                  cursor="pointer"
                />
              </Div>
            </Div>
          )}
        </Div>
      </Div>
      <HowItWorks
        supplier={supplier}
        title=""
        subtitle=""
        type="os-triple-play"
      />
    </Div>
  );
};
